import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

function CarouselDemo() {
  // Sample images
  const images = [
    "https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1615461066841-6116e61058f4?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1200&h=600&fit=crop"
  ];

  // Option 1: Basic Carousel
  const [currentSlide1, setCurrentSlide1] = useState(0);

  const nextSlide1 = () => {
    setCurrentSlide1((prev) => (prev + 1) % images.length);
  };

  const prevSlide1 = () => {
    setCurrentSlide1((prev) => (prev - 1 + images.length) % images.length);
  };

  // Auto-play for Option 1
  useEffect(() => {
    const interval = setInterval(nextSlide1, 4000);
    return () => clearInterval(interval);
  }, []);

  // Option 2: Carousel with Thumbnails
  const [currentSlide2, setCurrentSlide2] = useState(0);

  // Option 3: Multiple Items Carousel
  const [currentSlide3, setCurrentSlide3] = useState(0);

  const nextSlide3 = () => {
    setCurrentSlide3((prev) => (prev + 1) % (images.length - 2));
  };

  const prevSlide3 = () => {
    setCurrentSlide3((prev) => (prev - 1 + (images.length - 2)) % (images.length - 2));
  };

  // Option 4: Card Carousel
  const [currentSlide4, setCurrentSlide4] = useState(0);

  const services = [
    { title: "Oilfield Transport", desc: "Professional heavy hauling", img: images[0] },
    { title: "SAGD Support", desc: "Specialized oilfield services", img: images[1] },
    { title: "Lifting Services", desc: "Cranes and pickers", img: images[2] },
    { title: "Equipment Rental", desc: "Full fleet available", img: images[3] }
  ];

  const nextSlide4 = () => {
    setCurrentSlide4((prev) => (prev + 1) % services.length);
  };

  const prevSlide4 = () => {
    setCurrentSlide4((prev) => (prev - 1 + services.length) % services.length);
  };

  // Option 5: Full-Screen Hero Carousel
  const [currentSlide5, setCurrentSlide5] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide5((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-gray-900 min-h-screen">
      <div className="bg-black text-white py-4 px-6 sticky top-0 z-50">
        <h1 className="text-2xl font-bold text-green-400">Carousel Options Demo</h1>
        <p className="text-gray-400">Scroll to see different carousel styles</p>
      </div>

      {/* OPTION 1: Basic Auto-Play Carousel */}
      <section className="mb-16 border-b-4 border-green-500">
        <div className="bg-gray-800 px-6 py-4">
          <h2 className="text-3xl font-bold text-green-400">Option 1: Basic Auto-Play Carousel</h2>
          <p className="text-gray-300">Full-width with navigation arrows and dots</p>
        </div>
        <div className="px-6 py-12 bg-gray-900">
          <div className="max-w-6xl mx-auto relative">
            {/* Main Image */}
            <div className="relative h-96 rounded-lg overflow-hidden">
              <img
                src={images[currentSlide1]}
                alt={`Slide ${currentSlide1 + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-8 left-8 text-white">
                <h3 className="text-4xl font-bold mb-2">De Rose Transport</h3>
                <p className="text-xl text-green-400">Professional Heavy Hauling Services</p>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide1}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-green-500 text-white p-3 rounded-full transition-all"
            >
              <ChevronLeft size={32} />
            </button>
            <button
              onClick={nextSlide1}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-green-500 text-white p-3 rounded-full transition-all"
            >
              <ChevronRight size={32} />
            </button>

            {/* Dots Navigation */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide1(idx)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    idx === currentSlide1 ? 'bg-green-400 w-8' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OPTION 2: Carousel with Thumbnails */}
      <section className="mb-16 border-b-4 border-green-500">
        <div className="bg-gray-800 px-6 py-4">
          <h2 className="text-3xl font-bold text-green-400">Option 2: Carousel with Thumbnails</h2>
          <p className="text-gray-300">Main image with thumbnail navigation below</p>
        </div>
        <div className="px-6 py-12 bg-gray-900">
          <div className="max-w-6xl mx-auto">
            {/* Main Image */}
            <div className="relative h-96 rounded-lg overflow-hidden mb-4">
              <img
                src={images[currentSlide2]}
                alt={`Slide ${currentSlide2 + 1}`}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Thumbnails */}
            <div className="grid grid-cols-4 gap-4">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide2(idx)}
                  className={`relative h-24 rounded-lg overflow-hidden transition-all ${
                    idx === currentSlide2
                      ? 'ring-4 ring-green-400 scale-105'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OPTION 3: Multiple Items Carousel */}
      <section className="mb-16 border-b-4 border-green-500">
        <div className="bg-gray-800 px-6 py-4">
          <h2 className="text-3xl font-bold text-green-400">Option 3: Multiple Items Carousel</h2>
          <p className="text-gray-300">Shows 3 images at once, scrolls one at a time</p>
        </div>
        <div className="px-6 py-12 bg-gray-900">
          <div className="max-w-6xl mx-auto relative">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out gap-4"
                style={{ transform: `translateX(-${currentSlide3 * 33.33}%)` }}
              >
                {images.map((img, idx) => (
                  <div key={idx} className="min-w-[calc(33.33%-0.67rem)] h-64 rounded-lg overflow-hidden">
                    <img src={img} alt={`Item ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <button
              onClick={prevSlide3}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-green-500 hover:bg-green-400 text-white p-3 rounded-full transition-all"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextSlide3}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-green-500 hover:bg-green-400 text-white p-3 rounded-full transition-all"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      </section>

      {/* OPTION 4: Service Card Carousel */}
      <section className="mb-16 border-b-4 border-green-500">
        <div className="bg-gray-800 px-6 py-4">
          <h2 className="text-3xl font-bold text-green-400">Option 4: Service Card Carousel</h2>
          <p className="text-gray-300">Cards with image, title, and description</p>
        </div>
        <div className="px-6 py-12 bg-gray-900">
          <div className="max-w-6xl mx-auto">
            <div className="relative h-[500px]">
              {services.map((service, idx) => (
                <div
                  key={idx}
                  className={`absolute inset-0 transition-all duration-500 ${
                    idx === currentSlide4
                      ? 'opacity-100 translate-x-0'
                      : idx < currentSlide4
                      ? 'opacity-0 -translate-x-full'
                      : 'opacity-0 translate-x-full'
                  }`}
                >
                  <div className="bg-gray-800 rounded-lg overflow-hidden border-2 border-green-500/30 h-full flex flex-col md:flex-row">
                    <img
                      src={service.img}
                      alt={service.title}
                      className="w-full md:w-1/2 h-64 md:h-full object-cover"
                    />
                    <div className="p-8 flex flex-col justify-center">
                      <h3 className="text-4xl font-bold text-green-400 mb-4">{service.title}</h3>
                      <p className="text-gray-300 text-xl mb-6">{service.desc}</p>
                      <ul className="text-gray-300 space-y-2 mb-8">
                        <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Professional Service</li>
                        <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> 24/7 Availability</li>
                        <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Fully Certified</li>
                      </ul>
                      <button className="bg-green-500 hover:bg-green-400 text-gray-900 px-6 py-3 rounded-lg font-bold transition-all w-fit">
                        Learn More
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Navigation */}
              <button
                onClick={prevSlide4}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-green-500 text-white p-3 rounded-full transition-all z-10"
              >
                <ChevronLeft size={32} />
              </button>
              <button
                onClick={nextSlide4}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-green-500 text-white p-3 rounded-full transition-all z-10"
              >
                <ChevronRight size={32} />
              </button>

              {/* Dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {services.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide4(idx)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      idx === currentSlide4 ? 'bg-green-400 w-8' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OPTION 5: Full-Screen Hero Carousel */}
      <section className="mb-16">
        <div className="bg-gray-800 px-6 py-4">
          <h2 className="text-3xl font-bold text-green-400">Option 5: Full-Screen Hero Carousel</h2>
          <p className="text-gray-300">Dramatic full-height background slider with text overlay</p>
        </div>
        <div className="relative h-[600px] overflow-hidden">
          {images.map((img, idx) => (
            <div
              key={idx}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                idx === currentSlide5 ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img src={img} alt={`Hero ${idx + 1}`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/60"></div>
            </div>
          ))}

          {/* Content Overlay */}
          <div className="absolute inset-0 flex items-center justify-center text-center text-white z-10">
            <div className="max-w-4xl px-6">
              <h1 className="text-6xl font-black mb-4">De Rose Transport</h1>
              <p className="text-3xl font-bold text-green-400 mb-6">Family Owned & Operated</p>
              <p className="text-xl text-gray-200 mb-8">
                Specializing in Oilfield Hauling & SAGD Support
              </p>
              <div className="flex gap-4 justify-center">
                <button className="bg-green-500 hover:bg-green-400 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all">
                  Request a Quote
                </button>
                <button className="border-2 border-white hover:bg-white hover:text-gray-900 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all">
                  View Services
                </button>
              </div>
            </div>
          </div>

          {/* Progress Indicators */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide5(idx)}
                className={`h-1 rounded-full transition-all ${
                  idx === currentSlide5 ? 'bg-green-400 w-12' : 'bg-white/50 w-8'
                }`}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default CarouselDemo;
